/**
 * IndexedDB queue operations via idb.
 *
 * Queue items represent pending operations to sync with Snipe-IT.
 * Each item has: id, type, payload, createdAt, status
 */

import { openDB } from 'idb';

const DB_NAME = 'snipe-app';
const DB_VERSION = 3;
const STORE_NAME = 'queue';
const HISTORY_STORE = 'history';

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('status', 'status');
          store.createIndex('type', 'type');
          store.createIndex('createdAt', 'createdAt');
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains(HISTORY_STORE)) {
            const hist = db.createObjectStore(HISTORY_STORE, { keyPath: 'id', autoIncrement: true });
            hist.createIndex('syncedAt', 'syncedAt');
            hist.createIndex('type', 'type');
          }
        }
        if (oldVersion < 3) {
          // Recovery: create history store if it was missed during the v1→v2 upgrade
          if (!db.objectStoreNames.contains(HISTORY_STORE)) {
            const hist = db.createObjectStore(HISTORY_STORE, { keyPath: 'id', autoIncrement: true });
            hist.createIndex('syncedAt', 'syncedAt');
            hist.createIndex('type', 'type');
          }
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Add an item to the queue.
 * @param {'create_asset'|'checkout'|'checkin'|'update_asset'} type
 * @param {object} payload — the data to send to Snipe-IT
 * @returns {Promise<number>} — the auto-generated id
 */
export async function enqueue(type, payload, imageFile = null) {
  const db = await getDB();
  const item = {
    type,
    payload,
    createdAt: Date.now(),
    status: 'pending', // pending | syncing | done | error
    error: null,
  };
  if (imageFile) item.imageFile = imageFile;
  return db.add(STORE_NAME, item);
}

/**
 * Get all queue items, newest first.
 */
export async function getAll() {
  const db = await getDB();
  const items = await db.getAll(STORE_NAME);
  return items.sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Get all pending items.
 */
export async function getPending() {
  const db = await getDB();
  const items = await db.getAllFromIndex(STORE_NAME, 'status', 'pending');
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Update an item's status.
 */
export async function updateStatus(id, status, error = null) {
  const db = await getDB();
  const item = await db.get(STORE_NAME, id);
  if (!item) return;
  item.status = status;
  item.error = error;
  await db.put(STORE_NAME, item);
}

/**
 * Delete a single item.
 */
export async function remove(id) {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

/**
 * Delete all items with status 'done'.
 */
export async function clearDone() {
  const db = await getDB();
  const items = await db.getAllFromIndex(STORE_NAME, 'status', 'done');
  const tx = db.transaction(STORE_NAME, 'readwrite');
  for (const item of items) {
    tx.store.delete(item.id);
  }
  await tx.done;
}

/**
 * Get a single queue item by id.
 */
export async function getItem(id) {
  const db = await getDB();
  return db.get(STORE_NAME, id);
}

/**
 * Update an item's payload (for queue editing before sync).
 */
export async function updatePayload(id, payload) {
  const db = await getDB();
  const item = await db.get(STORE_NAME, id);
  if (!item) return;
  item.payload = payload;
  await db.put(STORE_NAME, item);
}

// ── History store ──

/**
 * Move a successfully synced item into the history store.
 * Adds a syncedAt timestamp; the original id is dropped (autoIncrement assigns a new one).
 */
export async function addToHistory(item) {
  const db = await getDB();
  const { id: _id, imageFile: _img, ...rest } = item;
  await db.add(HISTORY_STORE, { ...rest, syncedAt: Date.now() });
}

/**
 * Get all history items, newest first.
 */
export async function getHistory() {
  const db = await getDB();
  const items = await db.getAll(HISTORY_STORE);
  return items.sort((a, b) => b.syncedAt - a.syncedAt);
}

/**
 * Delete all history items.
 */
export async function clearHistory() {
  const db = await getDB();
  await db.clear(HISTORY_STORE);
}

/**
 * Count pending items.
 */
export async function pendingCount() {
  const db = await getDB();
  const items = await db.getAllFromIndex(STORE_NAME, 'status', 'pending');
  return items.length;
}
